import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sparkles, Package, HelpCircle, Factory, Loader2, X, CornerDownLeft } from "lucide-react";
import api from "@/lib/api";

/**
 * FLOWTALIS™ Universal Search — hybrid keyword + AI answer.
 *
 * Keyword pass: real-time matches from Brand Vault, Support Tickets, and
 * Production Tracker (reorders + turnkey inquiries). Debounced 220ms.
 *
 * AI pass: when the query looks like a natural-language question, Claude
 * Sonnet 4.5 grounds a single-sentence answer on the client's own scoped
 * data. Rendered as a distinct "AI Answer" block above the results.
 *
 * Dark-theme dropdown to sit inside the light portal header — reinforces
 * the matte-black + gold design system without redesigning the shell.
 */
const UniversalSearch = () => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ vault: [], tickets: [], production: [], ai_answer: null });
  const nav = useNavigate();
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // Close on click-outside
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Cmd/Ctrl-K focus shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) {
      setResults({ vault: [], tickets: [], production: [], ai_answer: null });
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const r = await api.get("/portal/search", { params: { q, ai: true }, signal: controller.signal });
        setResults(r.data);
      } catch (err) {
        if (err.name !== "CanceledError" && err.name !== "AbortError") {
          setResults({ vault: [], tickets: [], production: [], ai_answer: null });
        }
      } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(timer);
  }, [q]);

  const total = results.vault.length + results.tickets.length + results.production.length;
  const showEmpty = q.trim().length >= 2 && !loading && total === 0 && !results.ai_answer;

  const go = (path) => { setOpen(false); setQ(""); nav(path); };

  return (
    <div ref={boxRef} className="relative w-full md:max-w-md" data-testid="universal-search">
      <div className="relative">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ft-muted)] pointer-events-none z-10" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search vault, tickets, production…  or ask a question"
          data-testid="universal-search-input"
          style={{
            backgroundColor: "#FFFFFF",
            color: "#111111",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 0 0 1px rgba(200,162,74,0.15)",
          }}
          className="w-full pl-10 pr-10 h-11 rounded-full text-sm font-medium placeholder:text-[#8a8a8a] placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-[var(--ft-gold)] focus:border-transparent transition-all"
        />
        {q && (
          <button
            onClick={() => { setQ(""); inputRef.current?.focus(); }}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#8a8a8a] hover:text-[#111] z-10"
            aria-label="Clear"
            data-testid="universal-search-clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute z-50 top-12 left-0 right-0 rounded-2xl overflow-hidden shadow-2xl border border-white/10"
          style={{ background: "#0B0B0B" }}
          data-testid="universal-search-dropdown"
        >
          {/* AI Answer block */}
          {(loading || results.ai_answer) && (
            <div className="p-4 border-b border-white/8 bg-gradient-to-br from-[#141414] to-[#0B0B0B]">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={12} className="text-[var(--ft-gold)]" />
                <span className="text-[0.65rem] uppercase tracking-[0.24em] font-semibold text-[var(--ft-gold)]">
                  AI Answer
                </span>
              </div>
              {loading && !results.ai_answer ? (
                <div className="flex items-center gap-2 text-white/50 text-sm">
                  <Loader2 size={13} className="animate-spin" /> Thinking…
                </div>
              ) : (
                <p className="text-sm text-white/85 leading-relaxed" data-testid="universal-search-ai-answer">
                  {results.ai_answer}
                </p>
              )}
            </div>
          )}

          {/* File Results */}
          <div className="max-h-[420px] overflow-y-auto">
            {loading && total === 0 && !results.ai_answer && (
              <div className="p-6 text-center text-white/45 text-xs">
                <Loader2 size={14} className="animate-spin inline mr-2" /> Searching your workspace…
              </div>
            )}

            {results.vault.length > 0 && (
              <Section label="Brand Vault" icon={Package}>
                {results.vault.map((v) => (
                  <ResultRow
                    key={v.id}
                    testid={`search-vault-${v.id}`}
                    onClick={() => go("/portal/vault")}
                    title={v.title}
                    meta={`${v.category?.replace("_", " ")}${v.brand_name ? ` · ${v.brand_name}` : ""} · ${v.asset_type}`}
                  />
                ))}
              </Section>
            )}

            {results.tickets.length > 0 && (
              <Section label="Support Tickets" icon={HelpCircle}>
                {results.tickets.map((t) => (
                  <ResultRow
                    key={t.id}
                    testid={`search-ticket-${t.id}`}
                    onClick={() => go("/portal/support")}
                    title={t.subject}
                    meta={`${t.ticket_number} · ${t.status_label || t.status} · ${t.priority}`}
                  />
                ))}
              </Section>
            )}

            {results.production.length > 0 && (
              <Section label="Production Tracker" icon={Factory}>
                {results.production.map((p) => (
                  <ResultRow
                    key={p.id}
                    testid={`search-production-${p.id}`}
                    onClick={() => go("/portal/reorder")}
                    title={p.brand_name || p.reorder_number || "Order"}
                    meta={`${p.production_stage_label || "—"}${p.status_label ? ` · ${p.status_label}` : ""}`}
                  />
                ))}
              </Section>
            )}

            {showEmpty && (
              <div className="p-6 text-center text-white/45 text-xs" data-testid="universal-search-empty">
                No matches in your Brand Vault, tickets, or production timelines.
              </div>
            )}
          </div>

          <div className="px-4 py-2 border-t border-white/8 flex items-center justify-between text-[0.65rem] text-white/35 uppercase tracking-[0.2em]">
            <span>Portal Search</span>
            <span className="inline-flex items-center gap-1">
              <CornerDownLeft size={10} /> Enter to open
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ label, icon: Icon, children }) => (
  <div>
    <div className="px-4 pt-3 pb-1.5 flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.24em] font-semibold text-white/40">
      <Icon size={11} /> {label}
    </div>
    <div>{children}</div>
  </div>
);

const ResultRow = ({ title, meta, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors border-l-2 border-transparent hover:border-[var(--ft-gold)]"
  >
    <div className="text-sm text-white/90 truncate">{title}</div>
    <div className="text-[0.7rem] text-white/45 truncate mt-0.5">{meta}</div>
  </button>
);

export default UniversalSearch;
